import {useCallback, useRef, useState} from "react";
import type {V2AssistantMessage} from "../V2AssistantSidebar";
import type {EditorAssistantContext} from "./editorAssistantContext";

type AssistantAxios = {
    post: (url: string, body?: unknown, config?: unknown) => Promise<any>;
};

type UseEditorAssistantArgs = {
    authAxios?: AssistantAxios | null;
    getFreshToken?: (() => Promise<string | null | undefined>) | null;
    projectId?: string | null;
    compositionId?: string | null;
    surface?: "editor" | "home";
    onAction?: ((action: string) => void | Promise<void>) | null;
    getEditorContext?: (() => EditorAssistantContext | null | undefined) | null;
};

const newMessageId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const newConversationId = () => `assistant-${newMessageId()}`;

const assistantErrorMessage = (error: any) => {
    const status = error?.response?.status;
    if (status === 401) return "Your session expired. Please try again.";
    if (status === 429) return "The assistant is busy. Please wait a moment and try again.";
    return "Couldn't reach the assistant. Please try again.";
};

export const useEditorAssistant = ({
    authAxios,
    getFreshToken,
    projectId,
    compositionId,
    surface = "editor",
    onAction,
    getEditorContext,
}: UseEditorAssistantArgs) => {
    const [messages, setMessages] = useState<V2AssistantMessage[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestInFlight = useRef(false);
    const conversationId = useRef(newConversationId());

    const sendMessage = useCallback(async (rawMessage: string) => {
        const text = rawMessage.trim();
        if (!text || requestInFlight.current) return;

        const userMessage: V2AssistantMessage = {
            id: newMessageId(),
            role: "user",
            text,
        };
        const requestMessages = [...messages, userMessage]
            .slice(-20)
            .map(({role, text: content}) => ({role, content}));

        requestInFlight.current = true;
        setMessages((current) => [...current, userMessage]);
        setIsSending(true);
        setError(null);

        try {
            if (!authAxios || !getFreshToken) {
                throw new Error("Authenticated client unavailable");
            }

            const token = await getFreshToken();
            if (!token) {
                throw Object.assign(new Error("Authentication required"), {
                    response: {status: 401},
                });
            }

            const response = await authAxios.post(
                "/ai/assistant",
                {
                    conversationId: conversationId.current,
                    projectId: projectId || undefined,
                    compositionId: compositionId || undefined,
                    surface,
                    messages: requestMessages,
                    ...(surface === "editor" && getEditorContext
                        ? {editorContext: getEditorContext() ?? undefined}
                        : {}),
                },
                {headers: {Authorization: `Bearer ${token}`}}
            );
            const responseConversationId = String(response?.data?.conversationId ?? "").trim();
            if (responseConversationId) {
                conversationId.current = responseConversationId;
            }
            const responseText = String(response?.data?.text ?? "").trim();
            if (!responseText) {
                throw new Error("Assistant returned an empty response");
            }

            setMessages((current) => [
                ...current,
                {
                    id: newMessageId(),
                    role: "assistant",
                    text: responseText,
                },
            ]);

            const responseAction = typeof response?.data?.action === "string"
                ? response.data.action.trim()
                : "";
            if (responseAction && onAction) {
                try {
                    await onAction(responseAction);
                } catch (actionError) {
                    console.error("[Assistant] Action failed", actionError);
                }
            }
        } catch (requestError) {
            console.error("[Assistant] Request failed", requestError);
            setError(assistantErrorMessage(requestError));
        } finally {
            requestInFlight.current = false;
            setIsSending(false);
        }
    }, [authAxios, compositionId, getEditorContext, getFreshToken, messages, onAction, projectId, surface]);

    return {
        messages,
        sendMessage,
        isSending,
        error,
    };
};

export default useEditorAssistant;
